import React, { useState } from 'react';
import { posService } from '../../services/dataService';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toInputDate = (d, tz) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: tz || 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(d);

const fmtDate = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const firstOfMonth = (tz) => {
  const today = toInputDate(new Date(), tz);
  return today.slice(0, 7) + '-01';
};

const fmt = (n) => `₹${(n || 0).toFixed(2)}`;

// Escape CSV cell and wrap in quotes
const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

// Strip special characters from header names for clean CSV output
const csvHeader = (h) => cell(h.replace(/\(₹\)/g, '(INR)').replace(/₹/g, 'INR'));

const downloadCSV = (filename, headers, rows) => {
  const lines = [headers.map(csvHeader).join(','), ...rows.map(r => r.map(cell).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Report definitions ───────────────────────────────────────────────────────

const ALL_REPORTS = [
  {
    id: 'daily-sales',
    ownerOnly: true,
    icon: '📊',
    title: 'Daily Sales Summary',
    description: 'Total revenue per day with order count, taxes, discounts, and payment method breakdown.',
  },
  {
    id: 'invoice-detail',
    icon: '🧾',
    title: 'Full Invoice Report',
    description: 'Every invoice in the period with table, customer, items summary, all tax and discount details.',
  },
  {
    id: 'item-sales',
    icon: '🍽️',
    title: 'Item Sales Report',
    description: 'Quantity sold and revenue per menu item, ranked by total revenue.',
  },
  {
    id: 'payment-method',
    icon: '💳',
    title: 'Payment Method Breakdown',
    description: 'Revenue and invoice count split by Cash, Card, UPI, and Other.',
  },
  {
    id: 'gst-tax',
    ownerOnly: true,
    icon: '📋',
    title: 'GST / Tax Report',
    description: 'Tax-invoice-wise GST collected — taxable amount, GST %, GST amount, and total per invoice.',
  },
  {
    id: 'customer-sales',
    ownerOnly: true,
    icon: '👥',
    title: 'Customer Sales Report',
    description: 'Per-customer visit count, total spent, average order value, and total discounts received.',
  },
  {
    id: 'discount',
    ownerOnly: true,
    icon: '🏷️',
    title: 'Discount Report',
    description: 'All invoices where a discount was applied — type, amount, and impact on revenue.',
  },
  {
    id: 'order-type',
    icon: '🪑',
    title: 'Order Type Report',
    description: 'Revenue and order count split by Dine-in, Takeaway, and Counter order type.',
  },
  {
    id: 'hourly-sales',
    icon: '⏰',
    title: 'Hourly Sales Report',
    description: 'Revenue and order count by hour of day — identify peak and slow hours.',
  },
  {
    id: 'invalid-invoices',
    icon: '❌',
    title: 'Invalid Invoices Report',
    description: 'All invoices that were marked as invalid with original amount and date.',
  },
];

// ─── Per-report CSV generators ────────────────────────────────────────────────

const generators = {
  'daily-sales': (orders, range) => {
    const paid = orders.filter(o => o.paid_at && !o.is_invalid);
    const byDay = {};
    paid.forEach(o => {
      const day = new Date(o.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
      if (!byDay[day]) byDay[day] = { orders: 0, revenue: 0, discount: 0, gst: 0, sc: 0, cash: 0, card: 0, upi: 0, other: 0 };
      const d = byDay[day];
      d.orders++;
      d.revenue   += o.total_payable || 0;
      d.discount  += o.discount_amount || 0;
      d.gst       += o.gst_amount || 0;
      d.sc        += o.service_charge_amount || 0;
      const pm = (o.payment_method || 'other').toLowerCase();
      if (pm === 'cash') d.cash += o.total_payable || 0;
      else if (pm === 'card') d.card += o.total_payable || 0;
      else if (pm === 'upi') d.upi += o.total_payable || 0;
      else d.other += o.total_payable || 0;
    });
    const headers = ['Date', 'Orders', 'Revenue (₹)', 'Discount (₹)', 'GST (₹)', 'Service Charge (₹)', 'Cash (₹)', 'Card (₹)', 'UPI (₹)', 'Other (₹)'];
    const rows = Object.entries(byDay).map(([day, d]) => [
      day, d.orders, d.revenue.toFixed(2), d.discount.toFixed(2), d.gst.toFixed(2), d.sc.toFixed(2),
      d.cash.toFixed(2), d.card.toFixed(2), d.upi.toFixed(2), d.other.toFixed(2),
    ]);
    return { headers, rows, filename: `daily-sales-${range}.csv` };
  },

  'invoice-detail': (orders, range) => {
    const invoiced = orders.filter(o => o.invoice_number);
    const headers = ['Invoice #', 'Order #', 'Date', 'Table', 'Customer', 'Customer Phone', 'Items', 'Subtotal (₹)', 'Discount (₹)', 'GST (₹)', 'Service Charge (₹)', 'Total Payable (₹)', 'Payment Method', 'Paid', 'Status'];
    const rows = invoiced.map(o => {
      const table = o.restaurant_tables
        ? (o.restaurant_tables.custom_name || `Table ${o.restaurant_tables.table_number}`)
        : 'Counter';
      const items = (o.order_items || []).map(oi => `${oi.quantity}x ${oi.menu_items?.name}`).join('; ');
      return [
        o.invoice_number, o.order_number, fmtDate(o.billed_at || o.created_at),
        table, o.customer_name || '', o.customer_phone || '',
        items,
        (o.subtotal || 0).toFixed(2), (o.discount_amount || 0).toFixed(2),
        (o.gst_amount || 0).toFixed(2), (o.service_charge_amount || 0).toFixed(2),
        (o.total_payable || 0).toFixed(2),
        o.payment_method || '',
        o.paid_at ? 'Yes' : 'No',
        o.is_invalid ? 'INVALID' : 'Valid',
      ];
    });
    return { headers, rows, filename: `invoice-detail-${range}.csv` };
  },

  'item-sales': (orders, range) => {
    const valid = orders.filter(o => o.invoice_number && !o.is_invalid);
    const byItem = {};
    valid.forEach(o => {
      (o.order_items || []).forEach(oi => {
        const name = oi.menu_items?.name || 'Unknown';
        if (!byItem[name]) byItem[name] = { qty: 0, revenue: 0 };
        byItem[name].qty     += oi.quantity || 0;
        byItem[name].revenue += (oi.unit_price || 0) * (oi.quantity || 0);
      });
    });
    const sorted = Object.entries(byItem).sort((a, b) => b[1].revenue - a[1].revenue);
    const headers = ['Item Name', 'Quantity Sold', 'Revenue (₹)', 'Avg Unit Price (₹)'];
    const rows = sorted.map(([name, d]) => [
      name, d.qty, d.revenue.toFixed(2), (d.revenue / d.qty).toFixed(2),
    ]);
    return { headers, rows, filename: `item-sales-${range}.csv` };
  },

  'payment-method': (orders, range) => {
    const paid = orders.filter(o => o.paid_at && !o.is_invalid);
    const total = paid.reduce((s, o) => s + (o.total_payable || 0), 0);
    const byPM = {};
    paid.forEach(o => {
      const pm = (o.payment_method || 'other').toLowerCase();
      if (!byPM[pm]) byPM[pm] = { count: 0, revenue: 0 };
      byPM[pm].count++;
      byPM[pm].revenue += o.total_payable || 0;
    });
    const headers = ['Payment Method', 'Invoices', 'Revenue (₹)', '% of Revenue'];
    const rows = Object.entries(byPM).sort((a, b) => b[1].revenue - a[1].revenue).map(([pm, d]) => [
      pm.toUpperCase(), d.count, d.revenue.toFixed(2),
      total > 0 ? ((d.revenue / total) * 100).toFixed(1) + '%' : '0%',
    ]);
    return { headers, rows, filename: `payment-method-${range}.csv` };
  },

  'gst-tax': (orders, range) => {
    const taxable = orders.filter(o => o.invoice_number && !o.is_invalid && (o.gst_amount || 0) > 0);
    const headers = ['Invoice #', 'Date', 'Subtotal (₹)', 'Taxable Amount (₹)', 'GST % Applied', 'GST Amount (₹)', 'Total Payable (₹)', 'Customer'];
    const rows = taxable.map(o => [
      o.invoice_number, fmtDate(o.billed_at || o.created_at),
      (o.subtotal || 0).toFixed(2),
      ((o.subtotal || 0) - (o.discount_amount || 0)).toFixed(2),
      (o.applied_gst_percent || 0) + '%',
      (o.gst_amount || 0).toFixed(2),
      (o.total_payable || 0).toFixed(2),
      o.customer_name || '',
    ]);
    const totalGST = taxable.reduce((s, o) => s + (o.gst_amount || 0), 0);
    rows.push(['', 'TOTAL', '', '', '', totalGST.toFixed(2), '', '']);
    return { headers, rows, filename: `gst-tax-${range}.csv` };
  },

  'customer-sales': (orders, range) => {
    const invoiced = orders.filter(o => o.invoice_number && !o.is_invalid && (o.customer_name || o.customer_id));
    const byCustomer = {};
    invoiced.forEach(o => {
      const key = o.customer_name || o.customer_id;
      if (!byCustomer[key]) byCustomer[key] = { name: o.customer_name || '', phone: o.customer_phone || '', visits: 0, spent: 0, discount: 0 };
      const c = byCustomer[key];
      c.visits++;
      c.spent    += o.total_payable || 0;
      c.discount += o.discount_amount || 0;
    });
    const sorted = Object.values(byCustomer).sort((a, b) => b.spent - a.spent);
    const headers = ['Customer Name', 'Phone', 'Total Visits', 'Total Spent (₹)', 'Avg Order Value (₹)', 'Total Discount Received (₹)'];
    const rows = sorted.map(c => [
      c.name, c.phone, c.visits, c.spent.toFixed(2),
      (c.spent / c.visits).toFixed(2), c.discount.toFixed(2),
    ]);
    return { headers, rows, filename: `customer-sales-${range}.csv` };
  },

  'discount': (orders, range) => {
    const discounted = orders.filter(o => o.invoice_number && (o.discount_amount || 0) > 0);
    const headers = ['Invoice #', 'Date', 'Customer', 'Discount Type', 'Discount Amount (₹)', 'Gross Total (₹)', 'Net Payable (₹)', 'Status'];
    const rows = discounted.map(o => [
      o.invoice_number, fmtDate(o.billed_at || o.created_at),
      o.customer_name || '',
      o.discount_type || '',
      (o.discount_amount || 0).toFixed(2),
      (o.subtotal || 0).toFixed(2),
      (o.total_payable || 0).toFixed(2),
      o.is_invalid ? 'INVALID' : 'Valid',
    ]);
    const totalDiscount = discounted.reduce((s, o) => s + (o.discount_amount || 0), 0);
    rows.push(['', 'TOTAL DISCOUNT', '', '', totalDiscount.toFixed(2), '', '', '']);
    return { headers, rows, filename: `discounts-${range}.csv` };
  },

  'order-type': (orders, range) => {
    const invoiced = orders.filter(o => o.invoice_number && !o.is_invalid);
    const total = invoiced.reduce((s, o) => s + (o.total_payable || 0), 0);
    const byType = {};
    invoiced.forEach(o => {
      const t = o.order_type || 'UNKNOWN';
      if (!byType[t]) byType[t] = { count: 0, revenue: 0 };
      byType[t].count++;
      byType[t].revenue += o.total_payable || 0;
    });
    const headers = ['Order Type', 'Orders', 'Revenue (₹)', '% of Revenue'];
    const rows = Object.entries(byType).sort((a, b) => b[1].revenue - a[1].revenue).map(([t, d]) => [
      t.replace('_', ' '), d.count, d.revenue.toFixed(2),
      total > 0 ? ((d.revenue / total) * 100).toFixed(1) + '%' : '0%',
    ]);
    return { headers, rows, filename: `order-type-${range}.csv` };
  },

  'hourly-sales': (orders, range) => {
    const paid = orders.filter(o => o.paid_at && !o.is_invalid);
    const byHour = {};
    for (let h = 0; h < 24; h++) byHour[h] = { orders: 0, revenue: 0 };
    paid.forEach(o => {
      const h = new Date(o.paid_at).getHours();
      byHour[h].orders++;
      byHour[h].revenue += o.total_payable || 0;
    });
    const headers = ['Hour', 'Time Slot', 'Orders', 'Revenue (₹)'];
    const rows = Object.entries(byHour).map(([h, d]) => {
      const hour = parseInt(h);
      const from = hour === 0 ? '12:00 AM' : hour < 12 ? `${hour}:00 AM` : hour === 12 ? '12:00 PM' : `${hour - 12}:00 PM`;
      const to   = hour + 1 === 12 ? '12:00 PM' : hour + 1 < 12 ? `${hour + 1}:00 AM` : hour + 1 === 24 ? '12:00 AM' : `${hour + 1 - 12}:00 PM`;
      return [hour, `${from} – ${to}`, d.orders, d.revenue.toFixed(2)];
    });
    return { headers, rows, filename: `hourly-sales-${range}.csv` };
  },

  'invalid-invoices': (orders, range) => {
    const invalid = orders.filter(o => o.is_invalid && o.invoice_number);
    const headers = ['Invoice #', 'Order #', 'Date Billed', 'Table', 'Customer', 'Original Amount (₹)', 'Payment Method'];
    const rows = invalid.map(o => {
      const table = o.restaurant_tables
        ? (o.restaurant_tables.custom_name || `Table ${o.restaurant_tables.table_number}`)
        : 'Counter';
      return [
        o.invoice_number, o.order_number, fmtDate(o.billed_at || o.created_at),
        table, o.customer_name || '',
        (o.total_payable || 0).toFixed(2), o.payment_method || '',
      ];
    });
    return { headers, rows, filename: `invalid-invoices-${range}.csv` };
  },
};

// ─── Manager Activity helpers ─────────────────────────────────────────────────

const buildManagerActivityRows = (orders) => {
  const byManager = {};
  orders.forEach(o => {
    const mgId   = o.billed_by_manager_id || '__unassigned__';
    const mgName = o.managers?.name || 'Unassigned / Owner';
    if (!byManager[mgId]) byManager[mgId] = { name: mgName, invoices: 0, valid: 0, paid: 0, billed: 0, collected: 0 };
    const m = byManager[mgId];
    m.invoices++;
    if (!o.is_invalid) { m.valid++; m.billed += o.total_payable || 0; }
    if (o.paid_at && !o.is_invalid) { m.paid++; m.collected += o.total_payable || 0; }
  });
  return Object.values(byManager).sort((a, b) => b.billed - a.billed);
};

// ─── Shared date filter ───────────────────────────────────────────────────────

const useCardDates = (tz) => {
  const [activePreset, setActivePreset] = useState('month');
  const [startDate, setStartDate]       = useState(() => firstOfMonth(tz));
  const [endDate, setEndDate]           = useState(() => toInputDate(new Date(), tz));

  const applyPreset = (key) => {
    if (key === 'today')  { const t = toInputDate(new Date(), tz); setStartDate(t); setEndDate(t); }
    if (key === 'week')   { const d = new Date(); d.setDate(d.getDate() - 6); setStartDate(toInputDate(d, tz)); setEndDate(toInputDate(new Date(), tz)); }
    if (key === 'month')  { setStartDate(firstOfMonth(tz)); setEndDate(toInputDate(new Date(), tz)); }
    setActivePreset(key);
  };

  return { activePreset, startDate, setStartDate, endDate, setEndDate, applyPreset };
};

const CardDateFilter = ({ activePreset, startDate, setStartDate, endDate, setEndDate, applyPreset, onReset, tz }) => (
  <div style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: '14px', marginBottom: '14px' }}>
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
      {[
        { label: 'Today',      key: 'today'  },
        { label: 'This Week',  key: 'week'   },
        { label: 'This Month', key: 'month'  },
        { label: 'Custom',     key: 'custom' },
      ].map(({ label, key }) => (
        <button
          key={key}
          onClick={() => { applyPreset(key); if (onReset) onReset(); }}
          style={{
            padding: '5px 12px', borderRadius: '7px', border: 'none',
            background: activePreset === key ? '#222' : '#f3f4f6',
            color: activePreset === key ? '#fff' : '#555',
            fontWeight: '600', fontSize: '0.78rem', cursor: 'pointer',
          }}
        >
          {label}
        </button>
      ))}
      {activePreset !== 'custom' && (
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#888', alignSelf: 'center', whiteSpace: 'nowrap' }}>
          {startDate === endDate ? fmtDate(startDate) : `${fmtDate(startDate)} → ${fmtDate(endDate)}`}
        </span>
      )}
    </div>
    {activePreset === 'custom' && (
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '10px', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>From</label>
          <input
            type="date" value={startDate} max={endDate}
            onChange={e => { setStartDate(e.target.value); if (onReset) onReset(); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>To</label>
          <input
            type="date" value={endDate} min={startDate} max={toInputDate(new Date(), tz)}
            onChange={e => { setEndDate(e.target.value); if (onReset) onReset(); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1.5px solid #e5e7eb', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
          />
        </div>
      </div>
    )}
  </div>
);

// ─── Report card ──────────────────────────────────────────────────────────────

const ReportCard = ({ report, restaurant, isManager, managerId, managerName, accentGrad, setViewingReport }) => {
  const tz = restaurant.timezone || 'Asia/Kolkata';
  const { activePreset, startDate, setStartDate, endDate, setEndDate, applyPreset } = useCardDates(tz);
  const [loading, setLoading]       = useState(false);
  const [lastGenerated, setLast]    = useState(null);
  const [error, setError]           = useState('');

  const fetchAndGenerate = async () => {
    setLoading(true); setError('');
    const { data, error: err } = await posService.getOrdersForReport(
      restaurant.id, startDate, endDate, isManager ? managerId : null,
    );
    if (err || !data) { setLoading(false); setError('Failed to fetch data.'); return null; }
    const range  = `${startDate}_to_${endDate}`;
    const result = generators[report.id](data, range);
    if (result.rows.length === 0) { setLoading(false); setError('No data for selected range.'); return null; }
    setLoading(false);
    return result;
  };

  const handleDownload = async () => {
    const result = await fetchAndGenerate();
    if (!result) return;
    downloadCSV(result.filename, result.headers, result.rows);
    setLast({ rows: result.rows.length });
  };

  const handleView = async () => {
    const result = await fetchAndGenerate();
    if (!result) return;
    const dateLabel = startDate === endDate ? fmtDate(startDate) : `${fmtDate(startDate)} → ${fmtDate(endDate)}`;
    setViewingReport({ id: report.id, title: report.title, dateLabel, isManager, managerName, ...result });
  };

  return (
    <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '0' }}>
      <CardDateFilter
        activePreset={activePreset} startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate} applyPreset={applyPreset}
        onReset={() => setLast(null)} tz={tz}
      />

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: '800', fontSize: '1rem', color: '#222', marginBottom: '4px' }}>{report.title}</div>
        <div style={{ fontSize: '0.82rem', color: '#777', lineHeight: '1.5' }}>{report.description}</div>
      </div>

      {error && (
        <div style={{ fontSize: '0.78rem', color: '#dc2626', background: '#fef2f2', padding: '6px 10px', borderRadius: '7px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0', lineHeight: 1, fontSize: '1rem' }}>×</button>
        </div>
      )}

      {lastGenerated && (
        <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: '600', background: '#f0fdf4', padding: '4px 10px', borderRadius: '6px', display: 'inline-block', alignSelf: 'flex-start', marginBottom: '10px' }}>
          ✓ {lastGenerated.rows} row{lastGenerated.rows !== 1 ? 's' : ''}
        </div>
      )}

      <div style={{ marginTop: 'auto', display: 'flex', gap: '8px' }}>
        <button
          onClick={handleView} disabled={loading}
          style={{ flex: 1, padding: '11px 12px', borderRadius: '10px', border: '1.5px solid #e5e7eb', background: 'white', color: loading ? '#9ca3af' : '#333', fontWeight: '700', fontSize: '0.88rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
        >
          {loading ? <><div style={{ width: '13px', height: '13px', border: '2px solid #9ca3af', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Loading...</> : '👁 View'}
        </button>
        <button
          onClick={handleDownload} disabled={loading}
          style={{ flex: 1, padding: '11px 12px', borderRadius: '10px', border: 'none', background: loading ? '#e5e7eb' : accentGrad, color: loading ? '#9ca3af' : 'white', fontWeight: '700', fontSize: '0.88rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
        >
          ⬇ CSV
        </button>
      </div>
    </div>
  );
};

// ─── Manager Activity card ────────────────────────────────────────────────────

const ManagerActivityCard = ({ restaurant, accentGrad }) => {
  const tz = restaurant.timezone || 'Asia/Kolkata';
  const { activePreset, startDate, setStartDate, endDate, setEndDate, applyPreset } = useCardDates(tz);
  const [loading, setLoading]   = useState(false);
  const [rows, setRows]         = useState(null);
  const [error, setError]       = useState('');

  const handleLoad = async () => {
    setLoading(true); setError('');
    const { data, error: err } = await posService.getManagerActivityReport(restaurant.id, startDate, endDate);
    setLoading(false);
    if (err || !data) { setError('Failed to load manager activity data.'); return; }
    if (data.length === 0) { setError('No invoiced orders found for the selected range.'); return; }
    setRows(buildManagerActivityRows(data));
  };

  const handleDownload = () => {
    if (!rows) return;
    const range   = `${startDate}_to_${endDate}`;
    const headers = ['Manager', 'Invoices Generated', 'Valid Invoices', 'Paid', 'Total Billed (₹)', 'Total Collected (₹)', 'Pending (₹)'];
    const csvRows = rows.map(m => [m.name, m.invoices, m.valid, m.paid, m.billed.toFixed(2), m.collected.toFixed(2), (m.billed - m.collected).toFixed(2)]);
    downloadCSV(`manager-activity-${range}.csv`, headers, csvRows);
  };

  return (
    <div style={{ background: 'white', borderRadius: '14px', padding: '20px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', border: '1.5px solid #e0e7ff', marginBottom: '28px' }}>
      <CardDateFilter
        activePreset={activePreset} startDate={startDate} setStartDate={setStartDate}
        endDate={endDate} setEndDate={setEndDate} applyPreset={applyPreset}
        onReset={() => setRows(null)} tz={tz}
      />

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: rows ? '20px' : '0' }}>
        <div>
          <div style={{ fontWeight: '800', fontSize: '1rem', color: '#222', marginBottom: '3px' }}>Manager Activity Report</div>
          <div style={{ fontSize: '0.82rem', color: '#777' }}>Invoice stats per manager: Billed amount, collected, and pending for the selected period.</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {rows && (
            <button
              onClick={handleDownload}
              style={{ padding: '10px 16px', borderRadius: '10px', border: 'none', background: accentGrad, color: 'white', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}
            >
              ⬇ CSV
            </button>
          )}
          <button
            onClick={handleLoad} disabled={loading}
            style={{ padding: '10px 18px', borderRadius: '10px', border: rows ? '1.5px solid #FF6B00' : 'none', background: rows ? 'white' : accentGrad, color: rows ? '#FF6B00' : 'white', fontWeight: '700', fontSize: '0.85rem', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            {loading
              ? <><div style={{ width: '13px', height: '13px', border: '2px solid #9ca3af', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Loading...</>
              : rows ? '↻ Refresh' : '👁 View'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ fontSize: '0.82rem', color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', padding: '0', lineHeight: 1, fontSize: '1rem' }}>×</button>
        </div>
      )}

      {rows && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                {['Manager', 'Invoices', 'Valid', 'Paid', 'Total Billed', 'Collected', 'Pending'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f5f3ff', borderBottom: '2px solid #e0e7ff', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((m, ri) => {
                const pending = m.billed - m.collected;
                return (
                  <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#fafafa' }}>
                    <td style={{ padding: '11px 14px', fontWeight: '700', color: '#333', borderBottom: '1px solid #f3f4f6' }}>{m.name}</td>
                    <td style={{ padding: '11px 14px', color: '#555', borderBottom: '1px solid #f3f4f6' }}>{m.invoices}</td>
                    <td style={{ padding: '11px 14px', color: '#555', borderBottom: '1px solid #f3f4f6' }}>{m.valid}</td>
                    <td style={{ padding: '11px 14px', color: '#16a34a', fontWeight: '600', borderBottom: '1px solid #f3f4f6' }}>{m.paid}</td>
                    <td style={{ padding: '11px 14px', fontWeight: '700', color: '#333', borderBottom: '1px solid #f3f4f6' }}>{fmt(m.billed)}</td>
                    <td style={{ padding: '11px 14px', fontWeight: '700', color: '#16a34a', borderBottom: '1px solid #f3f4f6' }}>{fmt(m.collected)}</td>
                    <td style={{ padding: '11px 14px', fontWeight: '700', color: pending > 0 ? '#f59e0b' : '#aaa', borderBottom: '1px solid #f3f4f6' }}>{fmt(pending)}</td>
                  </tr>
                );
              })}
              <tr style={{ background: '#f5f3ff' }}>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#333', borderTop: '2px solid #e0e7ff' }}>Total</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#333', borderTop: '2px solid #e0e7ff' }}>{rows.reduce((s, m) => s + m.invoices, 0)}</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#333', borderTop: '2px solid #e0e7ff' }}>{rows.reduce((s, m) => s + m.valid, 0)}</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#16a34a', borderTop: '2px solid #e0e7ff' }}>{rows.reduce((s, m) => s + m.paid, 0)}</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#333', borderTop: '2px solid #e0e7ff' }}>{fmt(rows.reduce((s, m) => s + m.billed, 0))}</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#16a34a', borderTop: '2px solid #e0e7ff' }}>{fmt(rows.reduce((s, m) => s + m.collected, 0))}</td>
                <td style={{ padding: '11px 14px', fontWeight: '800', color: '#f59e0b', borderTop: '2px solid #e0e7ff' }}>{fmt(rows.reduce((s, m) => s + (m.billed - m.collected), 0))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const ReportsApp = ({ restaurant, userType = 'owner', managerId, managerName }) => {
  const isManager = userType === 'manager';
  const [viewingReport, setViewingReport] = useState(null);

  const visibleReports = isManager
    ? ALL_REPORTS.filter(r => !r.ownerOnly)
    : ALL_REPORTS;

  const accentGrad = isManager
    ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
    : 'linear-gradient(135deg, #FF4444, #FF6B00)';

  // ── Report table view ───────────────────────────────────────────────────────
  if (viewingReport) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setViewingReport(null)}
            style={{ background: '#f3f4f6', border: 'none', borderRadius: '10px', padding: '9px 16px', cursor: 'pointer', fontWeight: '700', fontSize: '0.88rem', color: '#333' }}
          >
            ← Back to Reports
          </button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '800', fontSize: '1.1rem', color: '#222' }}>{viewingReport.title}</div>
            <div style={{ fontSize: '0.78rem', color: '#888' }}>
              {viewingReport.dateLabel} &middot; {viewingReport.rows.length} row{viewingReport.rows.length !== 1 ? 's' : ''}
              {viewingReport.isManager && viewingReport.managerName && ` · ${viewingReport.managerName}`}
            </div>
          </div>
          <button
            onClick={() => downloadCSV(viewingReport.filename, viewingReport.headers, viewingReport.rows)}
            style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', background: accentGrad, color: 'white', fontWeight: '700', fontSize: '0.88rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            ⬇ Download CSV
          </button>
        </div>

        <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', border: '1px solid rgba(0,0,0,0.05)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr>
                {viewingReport.headers.map((h, i) => (
                  <th key={i} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f9fafb', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {viewingReport.rows.map((row, ri) => (
                <tr key={ri} style={{ background: ri % 2 === 0 ? 'white' : '#fafafa' }}>
                  {row.map((c, ci) => (
                    <td key={ci} style={{ padding: '11px 16px', color: '#333', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' }}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '700', background: accentGrad, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Reports
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          {isManager
            ? `Showing data for your invoices only · ${managerName || ''}`
            : 'Select a date range per report, then view or download'}
        </p>
      </div>

      {/* Manager Activity Report (owner only) */}
      {!isManager && (
        <ManagerActivityCard restaurant={restaurant} accentGrad={accentGrad} />
      )}

      {/* Report cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '18px' }}>
        {visibleReports.map(report => (
          <ReportCard
            key={report.id}
            report={report}
            restaurant={restaurant}
            isManager={isManager}
            managerId={managerId}
            managerName={managerName}
            accentGrad={accentGrad}
            setViewingReport={setViewingReport}
          />
        ))}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ReportsApp;
